import type { $Fetch } from 'nitropack'
import type { FetchOptions, FetchContext } from 'ofetch'

import { useAlertStore } from '@/store/alert'

import { ResponseCode, type ApiResponseType } from '@/types/service'
import { checkRequestIsValidIfNeeded } from '@/utils/request'
import { DEFAULT_LANG } from '@/constants/locales'
import { IS_DEV_MODE } from '@/constants/envs'

type SkipGeneralAlertList = { urlPattern: RegExp; otherCondition?: (context: FetchContext) => boolean }[]

const RETRY_COUNT = 3
const RETRY_DELAY_MS = 1000
const RETRY_STATUS_CODES = [408, 425, 500, 502, 503, 504]

export const createApiClient = (options?: FetchOptions) => {
  let nuxtApp = useNuxtApp()
  const config = useRuntimeConfig()
  const skipGeneralAlertList: SkipGeneralAlertList = [
    { urlPattern: /\/auth/ },
    { urlPattern: /\/promo/ },
    { urlPattern: /\/register/ },
    { urlPattern: /\/login/ },
    { urlPattern: /\/password\/recover/ },
    { urlPattern: /\/password\/reset/ },
    { urlPattern: /\/member$/, otherCondition: context => context.response?._data.code === ResponseCode.UNAUTHORIZED },
    { urlPattern: /\/member\/deletion\/\w+/ },
    { urlPattern: /\/member\/suspended/ },
    { urlPattern: /\/order$/ },
    { urlPattern: /\/order\/\d+$/, otherCondition: context => context.response?.status === 404 },
    { urlPattern: /\/order\/\d+\/edit/ },
    { urlPattern: /\/review\/\d+\/like/, otherCondition: context => context.response?._data?.code === ResponseCode.DUPLICATE_KEY },
  ]
  const baseURL = IS_DEV_MODE ? '/api/proxy' : config.public.apiBaseUrl

  /**
   * 因在 SSR 時無法取得 `nuxtApp` 的最新值，且直接 call `useNuxtApp` 有機率發生 "nuxt instance unavailable" 錯誤，
   * 故需透過 `tryUseNuxtApp` 來更新 `nuxtApp` 的值。
   */
  function updateNuxtAppInstanceIfNeeded() {
    const _nuxtApp = tryUseNuxtApp()

    if (import.meta.server && _nuxtApp) {
      nuxtApp = _nuxtApp
    }
  }

  return $fetch.create({
    baseURL,
    credentials: 'include',
    retry: RETRY_COUNT,
    retryStatusCodes: RETRY_STATUS_CODES,
    retryDelay: context => {
      const currentRetryCount = RETRY_COUNT - (context.options.retry || 0) + 1

      return currentRetryCount * RETRY_DELAY_MS
    },
    onRequest({ options, request }) {
      if (!checkRequestIsValidIfNeeded(request)) {
        throw new Error('Invalid request')
      }

      updateNuxtAppInstanceIfNeeded()

      const { localeProperties } = nuxtApp.$i18n
      const acceptLang = localeProperties.value.name ?? DEFAULT_LANG

      options.headers.delete('common')
      options.headers = new Headers(options.headers)
      options.headers.set('Accept-Language', acceptLang)
      if (import.meta.client) {
        options.headers.set('Accept-Timezone', getTimeZone())
      } else {
        options.headers.set('Server-Side-Request', 'true')
      }
    },
    // onRequestError({ request, options, error }) {},
    // onResponse({ request, options, response }) {},
    onResponseError(context) {
      const isErrorSkipped = RETRY_STATUS_CODES.includes(context.response.status) && context.options.retry !== 0

      if (isErrorSkipped) {
        return
      }

      // FIXME: 過濾掉檢查登入時的錯誤訊息，但應該有更乾淨的做法
      const isAlertSkipped = skipGeneralAlertList.some(item => item.urlPattern.test(context.response.url) && (item.otherCondition?.(context) ?? true))

      if (context.response?._data?.message && !isAlertSkipped) {
        useAlertStore().error(context.response._data.message)
      }
    },
    ...options,
  })
}

let client: ReturnType<typeof createApiClient>

const useApiClient = <T>(): $Fetch<ApiResponseType<T>> => {
  if (!client) {
    client = createApiClient()
  }
  return client
}

export default useApiClient
